<template>
  <c-container fluid class="d-flex content-center min-vh-100 login-container c-app c-dark-theme">
    <img class="logo" src="/img/logos/longboss.png" />
    <c-row>
      <c-col>
        <c-card-group>
          <c-card class="p-4 login-form">
            <c-card-body>
              <template v-if="isCheckingResetKey">
                <c-spinner color="success" size="lg" />
              </template>
              <template v-else>
                <template v-if="!resetValid">
                  <p class="h4 mb-4">Password reset</p>
                  <p class="lead">
                    Your password reset link has expired. Please enter your email address using the
                    <router-link class="link" :to="{ name: 'passwordForgot' }">forgot password</router-link>
                    page and we'll send you a secure link to reset your password.
                  </p>
                </template>
                <template v-else>
                  <c-form @submit.prevent="resetPassword">
                    <p class="h4 mb-4">Password reset</p>
                    <template v-if="!passwordResetSuccess">
                      <!--                      <p>Complete your password reset by entering a new password below.</p>-->
                      <p>Please enter a new password which contains -</p>
                      <ul class="text-left mb-0">
                        <li>At least one number</li>
                        <li>An uppercase letter</li>
                        <li>A lowercase letter</li>
                      </ul>
                      <c-alert v-if="error" show color="warning" close-button class="mt-4">
                        {{ error }}
                      </c-alert>
                      <div class="mt-4">
                        <c-row>
                          <c-col sm="12">
                            <c-input
                              :isValid="checkIfValid('password')"
                              :value.sync="$v.form.password.$model"
                              type="password"
                              placeholder="New password"
                              autocomplete="new-password"
                              invalidFeedback="Your password must be a minimum of 8 characters, contain at least one number, one uppercase and lowercase letter"
                              :disabled="isResettingPassword"
                            />
                          </c-col>
                          <c-col sm="12">
                            <c-input
                              :isValid="checkIfValid('confirmPassword')"
                              :value.sync="$v.form.confirmPassword.$model"
                              type="password"
                              placeholder="Confirm new password"
                              autocomplete="new-password"
                              invalid-feedback="Passwords must match"
                              :disabled="isResettingPassword"
                            />
                          </c-col>
                        </c-row>
                      </div>
                      <c-row class="mt-4">
                        <c-col col="12" class="text-center">
                          <c-button
                              @click="resetPassword"
                              type="submit"
                              class="px-4 w-100 btn-primary"
                          >
                            <CSpinner v-if="isResettingPassword" color="success" size="sm"/>
                            <span v-else>Reset password</span>
                          </c-button>
                        </c-col>
                      </c-row>
                    </template>
                    <template v-else>
                      <p class="lead">
                        Thank-you, your password has been changed, please
                        <router-link to="/login" class="link">login</router-link>
                        here.
                      </p>
                    </template>
                  </c-form>
                </template>
              </template>
            </c-card-body>
          </c-card>
        </c-card-group>
      </c-col>
    </c-row>
    <c-row class="mt-3">
      <c-col col="12" class="text-center">
        <span class="small">
          Don't have an account?
          <a class="px-0 link" href="https://koalaboss.com" target="_blank">Get one</a>
        </span>
      </c-col>
    </c-row>
  </c-container>
</template>

<script>
import { validationMixin } from 'vuelidate';
import {
  required, minLength, sameAs, helpers,
} from 'vuelidate/lib/validators';
import { buildEndpoint } from '../../libs/buildEndPoint';

export default {
  name: 'ResetPassword',
  components: {},
  mixins: [validationMixin],
  data() {
    return {
      error: null,
      isResettingPassword: false,
      isCheckingResetKey: true,
      user: null,
      fullName: '',
      resetValid: false,
      passwordResetSuccess: false,
      passwordResetComplete: false,
      passwordResetKey: null,
      form: {
        password: '',
        confirmPassword: '',
      },
    };
  },
  computed: {
    isValid() {
      return !this.$v.form.$invalid;
    },
  },
  created() {
    this.passwordResetKey = this.$route.params.passwordResetKey;
    this.checkPasswordResetKey();
  },
  validations: {
    form: {
      password: {
        required,
        minLength: minLength(8),
        strongPass: helpers.regex('strongPass', /(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).{8,}/),
      },
      confirmPassword: {
        required,
        sameAsPassword: sameAs('password'),
      },
    },
  },
  methods: {
    checkIfValid(fieldName) {
      const field = this.$v.form[fieldName];
      if (!field.$dirty) {
        return null;
      }
      return !(field.$invalid || field.$model === '');
    },
    checkPasswordResetKey() {
      setTimeout(() => {
        this.error = false;
        const data = {
          passwordResetKey: this.passwordResetKey,
        };
        this.$http
          .post(buildEndpoint('password/reset/check/'), data)
          .then((response) => {
            this.resetValid = response.data.success;
          })
          .catch(() => {
            this.resetValid = false;
            this.error = true;
          })
          .finally(() => {
            this.isCheckingResetKey = false;
          });
      }, 2000);
    },
    resetPassword() {
      this.$v.$touch();
      if (this.isValid) {
        this.isResettingPassword = true;
        setTimeout(() => {
          this.error = null;
          const data = {
            password: this.form.password,
            passwordResetKey: this.passwordResetKey,
          };
          this.$http
            .post(buildEndpoint('password/reset/save/'), data)
            .then((response) => {
              this.passwordResetSuccess = response.data.success;
              if (!response.data.success) {
                this.error = response.data.message;
              }
            })
            .catch((error) => {
              this.passwordResetSuccess = false;
              this.error = error.message;
            })
            .finally(() => {
              this.passwordResetComplete = true;
              this.isResettingPassword = false;
            });
        }, 1000);
      }
    },
  },
};
</script>

<style lang="scss" scoped="">
.invalid-feedback {
  text-align: left !important;
}
</style>
